/**
 * App chính M2: canvas three.js + HUD + các bảng điều khiển (Preact DOM phủ canvas).
 *
 * Nối dây: GameController (core) → VongLap (tick mỗi giờ game) →
 * GameScene + QuanLyKhach (render). UI chỉ đọc state và gửi lệnh.
 */
import * as THREE from 'three';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { datDuoc, coDuongDi, kichThuocKhuVuc, timLoaiNoiThat } from '../core/noi-that.js';
import { GameController } from './game-controller.js';
import { QuanLyKhach } from '../render/khach.js';
import { taoGameScene, type GameScene } from '../render/scene.js';
import { taoVongLap, type VongLap } from '../render/vong-lap.js';
import { useCapNhat } from './dung-chung.js';
import { Hud } from './components/Hud.js';
import { PanelChinhSach } from './components/PanelChinhSach.js';
import { PanelNhapHang } from './components/PanelNhapHang.js';
import { PanelNangCap } from './components/PanelNangCap.js';
import { PanelNoiThat } from './components/PanelNoiThat.js';
import { BaoCaoOfflineModal } from './components/BaoCaoOffline.js';
import { CaiDat } from './components/CaiDat.js';
import { Tutorial } from './components/Tutorial.js';
import { BanDoTheGioi } from './components/BanDoTheGioi.js';
import { MiniMap } from './components/MiniMap.js';
import { PanelNhanVien } from './components/PanelNhanVien.js';
import { PanelNhiemVu } from './components/PanelNhiemVu.js';
import { PanelNpc } from './components/PanelNpc.js';
import { PanelThanhTuu } from './components/PanelThanhTuu.js';
import { PanelCosmetic } from './components/PanelCosmetic.js';
import { ChinhSachRiengTu } from './components/ChinhSachRiengTu.js';
import { BaoCaoCrashModal } from './components/BaoCaoCrashModal.js';
import { Toasts } from './components/Toast.js';
import { caiDatBatLoi } from '../crash.js';

interface BoRender {
  scene: GameScene;
  quanLyKhach: QuanLyKhach;
  vongLap: VongLap;
  bongDat: THREE.Mesh;
}

const CAC_BANG: { ma: string; nhan: string; icon: string }[] = [
  { ma: 'gia', nhan: 'Chính sách giá', icon: '💲' },
  { ma: 'nhap', nhan: 'Ngưỡng nhập hàng', icon: '📦' },
  { ma: 'nangcap', nhan: 'Nâng cấp', icon: '🏪' },
  { ma: 'noithat', nhan: 'Nội thất', icon: '🪑' },
  { ma: 'bando', nhan: 'Bản đồ thế giới', icon: '🗺️' },
  { ma: 'nhiemvu', nhan: 'Nhiệm vụ', icon: '📋' },
  { ma: 'npc', nhan: 'Người quen', icon: '👥' },
  { ma: 'thanhtuu', nhan: 'Thành tựu', icon: '🏆' },
  { ma: 'cosmetic', nhan: 'Trang trí', icon: '🎨' },
  { ma: 'nhanvien', nhan: 'Nhân viên', icon: '🧑‍💼' },
  { ma: 'riengtu', nhan: 'Riêng tư', icon: '🔒' },
  { ma: 'caidat', nhan: 'Cài đặt', icon: '⚙️' },
];

export function App() {
  const controller = useMemo(() => new GameController(), []);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const boRender = useRef<BoRender | null>(null);
  const [bangMo, setBangMo] = useState<string | null>(null);
  const [cheDoDat, setCheDoDat] = useState<string | null>(null);
  const [loiDat, setLoiDat] = useState('');
  const [loiKhoiDong, setLoiKhoiDong] = useState('');
  const [soKhach, setSoKhach] = useState(0);
  const soBoCucBanDau = useRef(controller.state.boCuc.length);
  const capDaVe = useRef(-1);
  const boCucDaVe = useRef('');
  const khuDaVe = useRef('');
  const thoiTietDaVe = useRef('');
  const nhanVatDaVe = useRef('');
  useCapNhat(controller);

  // Khởi tạo scene + vòng lặp một lần.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let scene: GameScene;
    try {
      scene = taoGameScene(canvas);
    } catch (e) {
      setLoiKhoiDong(e instanceof Error ? e.message : String(e));
      return;
    }
    const quanLyKhach = new QuanLyKhach(scene.scene);
    const bongDat = new THREE.Mesh(
      new THREE.BoxGeometry(1, 0.12, 1),
      new THREE.MeshBasicMaterial({ color: 0x33dd33, transparent: true, opacity: 0.45 }),
    );
    bongDat.visible = false;
    scene.scene.add(bongDat);

    const vongLap = taoVongLap({
      scene,
      quanLyKhach,
      layTrangThai: () => ({ state: controller.state, duLieu: controller.duLieu }),
      trenMoiGioGame: () => controller.tickMoiGio(),
    });

    boRender.current = { scene, quanLyKhach, vongLap, bongDat };
    vongLap.datTocDo(controller.caiDat.tocDo);
    vongLap.batDau();
    // M3: bắt lỗi toàn cục để báo crash ẩn danh.
    caiDatBatLoi();
    // Mở khóa audio ở cử chỉ đầu tiên (chính sách autoplay).
    const moKhoa = (): void => {
      controller.moKhoaAmThanh();
      window.removeEventListener('pointerdown', moKhoa);
    };
    window.addEventListener('pointerdown', moKhoa);
    // Tay cầm debug cho smoke test / QA (không chứa bí mật).
    (window as unknown as { __idleShop?: unknown }).__idleShop = {
      controller,
      laySoKhach: () => quanLyKhach.soKhach(),
    };

    const trenResize = (): void => scene.resize();
    window.addEventListener('resize', trenResize);
    scene.resize();
    const idDem = window.setInterval(() => {
      setSoKhach(quanLyKhach.soKhach());
    }, 1000);

    return () => {
      window.removeEventListener('resize', trenResize);
      window.clearInterval(idDem);
      vongLap.dung();
      scene.huy();
      boRender.current = null;
    };
  }, [controller]);

  // Vẽ lại khu vực / nội thất khi state đổi (cấp hoặc bố cục).
  useEffect(() => {
    const br = boRender.current;
    if (!br) return;
    const { state, duLieu } = controller;
    const khoaBoCuc = state.boCuc.map((m) => `${m.uid}:${m.ma}:${m.x},${m.y}`).join('|');
    if (capDaVe.current !== state.capCuaHang || khuDaVe.current !== state.khuHienTai) {
      capDaVe.current = state.capCuaHang;
      khuDaVe.current = state.khuHienTai;
      br.scene.veLaiKhuVuc(state.capCuaHang, duLieu, state.khuHienTai);
    }
    // M3: thời tiết theo khu hiện tại.
    const ttKhu = state.thoiTiet[state.khuHienTai]?.loai ?? null;
    const khoaTt = `${state.khuHienTai}:${ttKhu ?? 'khong'}:${controller.caiDat.tatHieuUng}`;
    if (thoiTietDaVe.current !== khoaTt) {
      thoiTietDaVe.current = khoaTt;
      br.scene.datThoiTiet(ttKhu, controller.caiDat.tatHieuUng || controller.caiDat.giamChuyenDong);
    }
    // M3: nhân viên + NPC trong scene (chỉ vẽ lại khi đổi).
    const khoaNv = `${state.nhanVien.thuNgan ? 1 : 0}${state.nhanVien.xepKe ? 1 : 0}:${Object.keys(state.npcThanThiet).length}`;
    if (nhanVatDaVe.current !== khoaNv) {
      nhanVatDaVe.current = khoaNv;
      const soNpc = Math.min(3, Object.keys(state.npcThanThiet).length + 1);
      br.scene.datNhanVat(state.nhanVien.thuNgan ? 1 : 0, state.nhanVien.xepKe ? 1 : 0, soNpc);
    }
    if (boCucDaVe.current !== khoaBoCuc) {
      boCucDaVe.current = khoaBoCuc;
      br.scene.veLaiNoiThat(state.boCuc, state.capCuaHang, duLieu);
      br.quanLyKhach.xoaTatCa();
    }
  });

  // Áp dụng cài đặt render.
  useEffect(() => {
    const br = boRender.current;
    if (!br) return;
    const cd = controller.caiDat;
    br.scene.datBloom(cd.bloom && !cd.giamChuyenDong);
    br.scene.datDoPhanGiaiDayDu(cd.doPhanGiaiDayDu);
    br.vongLap.datTocDo(cd.tocDo);
  });

  const datTocDo = (t: number): void => {
    controller.datCaiDat({ tocDo: t });
  };

  // Bóng ma xem trước khi đặt nội thất (dùng hàm thuần của core để kiểm).
  const capNhatBongDat = (clientX: number, clientY: number): void => {
    const br = boRender.current;
    if (!br || !cheDoDat) {
      if (br) br.bongDat.visible = false;
      return;
    }
    const o = br.scene.diemAnhSangO(clientX, clientY);
    const { state, duLieu } = controller;
    if (!o) {
      br.bongDat.visible = false;
      return;
    }
    const loai = timLoaiNoiThat(cheDoDat, duLieu);
    const kv = kichThuocKhuVuc(state.capCuaHang, duLieu);
    const datDuocO = datDuoc({ ma: cheDoDat, x: o.x, y: o.y }, state.boCuc, kv, duLieu);
    const boCucThu = [...state.boCuc, { uid: -1, ma: cheDoDat, x: o.x, y: o.y }];
    const duongDiOk = datDuocO && coDuongDi(boCucThu, kv, duLieu);
    br.bongDat.visible = true;
    br.bongDat.scale.set(loai.rong, 1, loai.cao);
    const wx = o.x - kv.rong / 2 + loai.rong / 2;
    const wz = o.y - kv.cao / 2 + loai.cao / 2;
    br.bongDat.position.set(wx, 0.06, wz);
    (br.bongDat.material as THREE.MeshBasicMaterial).color.set(duongDiOk ? 0x33dd33 : 0xdd3333);
  };

  const bamDatVaoO = (clientX: number, clientY: number): void => {
    const br = boRender.current;
    if (!br || !cheDoDat) return;
    const o = br.scene.diemAnhSangO(clientX, clientY);
    if (!o) return;
    try {
      controller.datNoiThatMoi(cheDoDat, o.x, o.y);
      setLoiDat('');
    } catch (e) {
      setLoiDat(e instanceof Error ? e.message : String(e));
    }
  };

  if (loiKhoiDong) {
    return (
      <main
        style={{
          fontFamily: 'system-ui, sans-serif',
          maxWidth: '42rem',
          margin: '4rem auto',
          padding: '0 1rem',
          textAlign: 'center',
        }}
      >
        <h1>idle-shop</h1>
        <p>⚠️ {loiKhoiDong}</p>
      </main>
    );
  }

  const coChu = controller.caiDat.coChuLon;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        background: '#14100a',
        fontSize: coChu ? '18px' : undefined,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          display: 'block',
          cursor: cheDoDat ? 'crosshair' : 'default',
        }}
        onPointerMove={(e) => capNhatBongDat(e.clientX, e.clientY)}
        onPointerDown={(e) => bamDatVaoO(e.clientX, e.clientY)}
      />
      <Hud controller={controller} soKhach={soKhach} datTocDo={datTocDo} />

      {/* Thanh công cụ bên trái */}
      <div
        style={{
          position: 'absolute',
          left: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          zIndex: 10,
        }}
      >
        {CAC_BANG.map((b) => (
          <button
            key={b.ma}
            title={b.nhan}
            onClick={() => setBangMo(bangMo === b.ma ? null : b.ma)}
            style={{
              width: '44px',
              height: '44px',
              fontSize: '20px',
              borderRadius: '10px',
              border: '1px solid #8a6a3a',
              background: bangMo === b.ma ? '#c98a2e' : 'rgba(20, 14, 8, 0.85)',
              cursor: 'pointer',
            }}
          >
            {b.icon}
          </button>
        ))}
      </div>

      {/* Bảng điều khiển */}
      {bangMo && (
        <div
          style={{
            position: 'absolute',
            left: '60px',
            top: '64px',
            width: '300px',
            maxHeight: 'calc(100% - 140px)',
            overflowY: 'auto',
            zIndex: 10,
          }}
        >
          {bangMo === 'gia' && <PanelChinhSach controller={controller} />}
          {bangMo === 'nhap' && <PanelNhapHang controller={controller} />}
          {bangMo === 'nangcap' && (
            <PanelNangCap
              controller={controller}
              baoDaNangCap={() => {
                /* scene tự vẽ lại qua effect */
              }}
            />
          )}
          {bangMo === 'noithat' && (
            <PanelNoiThat
              controller={controller}
              cheDoDat={cheDoDat}
              chonCheDoDat={(ma) => {
                setCheDoDat(ma);
                setLoiDat('');
              }}
              loiDat={loiDat}
              baoDaDat={() => {
                /* scene tự vẽ lại qua effect */
              }}
            />
          )}
          {bangMo === 'bando' && <BanDoTheGioi controller={controller} />}
          {bangMo === 'nhiemvu' && <PanelNhiemVu controller={controller} />}
          {bangMo === 'npc' && <PanelNpc controller={controller} />}
          {bangMo === 'thanhtuu' && <PanelThanhTuu controller={controller} />}
          {bangMo === 'cosmetic' && <PanelCosmetic controller={controller} />}
          {bangMo === 'nhanvien' && <PanelNhanVien controller={controller} />}
          {bangMo === 'riengtu' && <ChinhSachRiengTu controller={controller} />}
          {bangMo === 'caidat' && <CaiDat controller={controller} />}
        </div>
      )}

      {cheDoDat && (
        <div
          style={{
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(20, 14, 8, 0.9)',
            color: '#f5e9d0',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            zIndex: 10,
          }}
        >
          Bấm vào ô trống để đặt {timLoaiNoiThat(cheDoDat, controller.duLieu).ten} —{' '}
          <button
            onClick={() => setCheDoDat(null)}
            style={{
              background: 'transparent',
              color: '#c98a2e',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            Hủy
          </button>
        </div>
      )}

      {!controller.state.tutorialXong && (
        <Tutorial
          controller={controller}
          moBang={(ten) => setBangMo(ten)}
          soBoCucBanDau={soBoCucBanDau.current}
        />
      )}

      {controller.baoCaoOffline && (
        <BaoCaoOfflineModal controller={controller} baoCao={controller.baoCaoOffline} />
      )}

      <MiniMap controller={controller} moBanDo={() => setBangMo('bando')} />
      <Toasts />
      <BaoCaoCrashModal controller={controller} />
    </div>
  );
}
